// src/pages/AudioSessions.tsx - FIXED VERSION

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mic, FileText, Play, Pause, Building2, Bot, Phone, PhoneIncoming, PhoneOutgoing, PhoneForwarded, Hash } from 'lucide-react';
import { sessionsService } from 'api/services/sessions.service';
import { callsService } from 'api/services/calls.service';
import { recordingsService } from 'api/services/recordings.service';
import { organizationsService } from 'api/services/organizations.service';
import { AudioSession, PaginatedResponse, FilterParams } from 'types';
import { useApi } from 'hooks/useApi';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import ErrorAlert from 'components/Common/ErrorAlert';
import Pagination from 'components/Common/Pagination';
import AudioPlayer from 'components/Common/AudioPlayer';
import AudioSessionsFilterBar from 'components/Filters/AudioSessionsFilterBar';
import AudioSessionModal from 'components/Modals/AudioSessionModal';
import OrganizationFilter from 'components/Filters/OrganizationFilter';
import { formatDate, formatDuration, getDefaultDateRange, categorizeApiError } from 'utils/helpers';
import { ITEMS_PER_PAGE, FEATURE_FLAGS } from 'utils/constants';

const AudioSessions: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSession, setSelectedSession] = useState<AudioSession | null>(null);
  // Keyed by rowKey(), never by session.id directly.
  //
  // Comparing the active id straight against a row's own id looks correct, and
  // is — right up until the API omits that field. Then every row computes
  // `undefined === undefined`, which is TRUE, and pressing play opens the player
  // on every call at once. The tvoice backends are a different service from
  // cpass and do not return the same shape, so the field cannot be assumed.
  const [activePlayingId, setActivePlayingId] = useState<string | null>(null);
  const [recordingUrls, setRecordingUrls] = useState<Record<string, string | null>>({});
  const [loadingPlayIds, setLoadingPlayIds] = useState<Set<string>>(new Set());
  // Feature Flag: REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
  // When enabled, defaults to current organization ID, otherwise 'all'
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>(
    FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
      ? (currentOrganization?.id || '')
      : 'all'
  );
  // ?call=<callSid> — how a lead links back to the call that produced it.
  // The server already accepts callId; this only reads it off the URL.
  //
  // The default date range is dropped when a call is named: the link has to work
  // for a call from last month, and silently intersecting it with "the last two
  // days" would show an empty page for a lead that plainly exists.
  const [searchParams, setSearchParams] = useSearchParams();
  const callParam = searchParams.get('call') || '';
  const [filters, setFilters] = useState<FilterParams>(
    callParam
      ? { perPage: ITEMS_PER_PAGE, callId: callParam }
      : { perPage: ITEMS_PER_PAGE, ...getDefaultDateRange() },
  );

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      // When REQUIRE_ORG_SELECTION is enabled, selectedOrgId should never be 'all' or empty
      // Return undefined for 'all' or empty string to prevent invalid API calls
      if (selectedOrgId === 'all' || !selectedOrgId) {
        return undefined;
      }
      return selectedOrgId;
    }
    // For other roles, only return valid organization IDs (not synthetic)
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  // Check if organization selection is required but not selected
  // service_admin belongs here with the other two. getOrganizationId() already
  // treats all three the same — it returns undefined until an organisation is
  // picked — but only master and super admins were ever ASKED to pick one. A
  // service provider therefore got no prompt and no organisationId, and the
  // backend's no-organisation path reads the LOCAL call tables rather than
  // federating to the switches, which is where this estate's calls actually
  // live. The page came back empty and said nothing.
  const needsOrgSelection = FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA &&
    (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') &&
    (!selectedOrgId || selectedOrgId === 'all');

  const { data, loading, error, refetch } = useApi<PaginatedResponse<AudioSession>>(
    () => {
      // Skip API call if organization selection is required but not selected
      if (needsOrgSelection) {
        return Promise.resolve({ success: true, data: { data: [], total: 0, totalPages: 0, currentPage: 1 } });
      }
      return sessionsService.getAudioSessions({ ...filters, page: currentPage, organizationId: getOrganizationId() });
    },
    [currentPage, filters.transferType, filters.callerId, filters.callId, filters.dateFrom, filters.dateTo, selectedOrgId, currentOrganization?.id, needsOrgSelection]
  );

  const handleFilterChange = (newFilters: FilterParams) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  /**
   * Play a row's recording without leaving the list.
   *
   * Two mechanisms exist and both are needed. The presigned S3 URL is the one
   * to prefer: the object key contains the caller's own organisation, so a call
   * id belonging to another tenant resolves to nothing rather than to their
   * audio. Where cpass holds no copy — S3 storage switched off, or a call older
   * than the backfill — we fall back to the recording URL on the call record
   * with the session JWT appended, which is what the Africa and Europe backends
   * accept. A row with neither is marked known-missing and its button disabled,
   * so it is asked for once and not on every click.
   */
  /**
   * A stable identity for one row, or null when the row has none.
   *
   * Falls back through the identifiers these backends actually return. Null is
   * returned rather than a made-up value so that isPlaying can refuse to match:
   * an unidentifiable row must open NO player, never all of them.
   */
  const rowKey = (s: AudioSession): string | null => {
    // `as any` because the declared type promises `id: number` while the tvoice
    // backends may not send it at all — the mismatch between the declared shape
    // and the real one is precisely the bug being fixed here.
    const k = (s as any)?.id ?? (s as any)?.call_log_id
           ?? (s as any)?.call_id ?? (s as any)?.session_ref;
    return k === undefined || k === null || k === '' ? null : String(k);
  };

  const handleTogglePlay = async (session: AudioSession) => {
    const key = rowKey(session);
    if (!key) return;   // nothing identifies this row; opening every player is worse

    if (activePlayingId === key) {
      setActivePlayingId(null);
      return;
    }

    const cached = recordingUrls[key];
    if (cached) {
      setActivePlayingId(key);
      return;
    }
    if (cached === null) return; // known-missing — do not ask again

    setLoadingPlayIds((prev) => new Set(prev).add(key));
    try {
      let playable: string | null = null;

      const callId = session.call_id;
      // The row may already carry the recording URL; only pay for a detail
      // fetch when it does not. Whatever we have goes to the SHARED resolver,
      // which decides the order — CDN first when the stored URL is one, cpass
      // otherwise. This page used to hardcode its own order (cpass, then
      // stored), which on the tvoice portals meant a failing round trip before
      // the mechanism that actually works.
      let recordingUrl: string = (session as any)?.recording_url || '';
      if (!recordingUrl && session.call_log_id) {
        const res = await callsService.getCallDetail(session.call_log_id, getOrganizationId());
        recordingUrl = res.success && res.data?.recording_url ? res.data.recording_url : '';
      }

      playable = await recordingsService.resolvePlayableUrl(callId, recordingUrl, getOrganizationId());

      if (!playable) {
        setRecordingUrls((prev) => ({ ...prev, [key]: null }));
        return;
      }

      setRecordingUrls((prev) => ({ ...prev, [key]: playable }));
      setActivePlayingId(key);
    } catch (err) {
      console.error('Failed to load recording:', err);
      setRecordingUrls((prev) => ({ ...prev, [key]: null }));
    } finally {
      setLoadingPlayIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Categorize error for better handling
  const categorizedError = categorizeApiError(error);
  const isOrgNotFoundError = categorizedError?.type === 'organization_not_found';

  if (loading) return <LoadingSpinner />;
  // For "Organization not found" error, continue rendering to show organization filter
  // For other errors, show full error page unless it's a categorized error that should keep UI visible
  if (error && !isOrgNotFoundError && categorizedError?.type !== 'permission') {
    return <ErrorMessage message={error} onRetry={refetch} />;
  }
  if (!data && !categorizedError) return null;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-white">
          AI Calls {data ? <span className="text-gray-500 dark:text-gray-400 text-base lg:text-xl">({data.total})</span> : ''}
        </h1>
        <button
          onClick={refetch}
          className="px-3 lg:px-4 py-2 text-xs lg:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
        >
          Refresh
        </button>
      </div>

      {/* Organization Filter - For master_admin, super_admin, and service_admin */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              // Feature Flag: When REQUIRE_ORG_SELECTION is enabled, don't allow 'all'
              if (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA) {
                setSelectedOrgId(value || selectedOrgId); // Keep current if empty
              } else {
                setSelectedOrgId(value || 'all'); // Legacy: allow 'all'
              }
              setCurrentPage(1);
            }}
            className="w-full md:w-96"
            requireSelection={FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show friendly message when organization selection is required */}
      {needsOrgSelection && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300">Select an Organization</h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                Please select an organization from the dropdown above to view AI calls.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Show error message if there's an error, but keep filters visible for certain error types */}
      {!needsOrgSelection && categorizedError && (
        <ErrorAlert
          error={categorizedError}
          onRetry={refetch}
          className="mb-4"
        />
      )}

      {/* Filters - Only show if we have data or if error allows UI to remain visible */}
      {/* A page showing one call has to say why, or it reads as data loss. */}
      {callParam && (
        <div className="mb-3 flex items-center justify-between gap-3 flex-wrap text-sm rounded-lg px-3 py-2
                        bg-indigo-50 text-indigo-900 dark:bg-indigo-900/20 dark:text-indigo-200">
          <span>Showing one call — <span className="font-mono text-xs">{callParam}</span>, opened from a lead.</span>
          <button
            type="button"
            className="underline underline-offset-2 font-medium"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('call');
              setSearchParams(next, { replace: true });
              // The date range was deliberately dropped while a call was named;
              // put it back rather than loading every session ever recorded.
              setFilters({ perPage: ITEMS_PER_PAGE, ...getDefaultDateRange() });
            }}
          >
            Show all calls
          </button>
        </div>
      )}
      {!needsOrgSelection && !categorizedError && <AudioSessionsFilterBar filters={filters} onFilterChange={handleFilterChange} />}

      {!needsOrgSelection && !categorizedError && data && <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {/* Mobile Card View */}
        <div className="lg:hidden">
          {data.data.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.data.map((session) => (
                <div
                  key={rowKey(session) ?? `row-${session.call_log_id}`}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition cursor-pointer"
                  onClick={() => setSelectedSession(session)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {session.call_type && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${session.call_type === 'IN'
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400'
                            : 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-400'
                          }`}>
                          {session.call_type === 'IN' ? <PhoneIncoming className="w-3 h-3" /> : <PhoneOutgoing className="w-3 h-3" />}
                          {session.call_type === 'IN' ? 'Incoming' : 'Outgoing'}
                        </span>
                      )}
                      {session.transfer_type && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${session.transfer_type === 'ai' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-400' : 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400'
                          }`}>
                          {session.transfer_type === 'ai' ? <Bot className="w-3 h-3" /> : <Hash className="w-3 h-3" />}
                          {session.transfer_type === 'ai' ? 'AI' : 'Hotkey'}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDuration(session.audio_duration_ms)}
                    </span>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 dark:text-gray-400 text-xs">From:</span>
                      <span className="text-gray-900 dark:text-white font-medium">{session.caller_id || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 dark:text-gray-400 text-xs">To:</span>
                      <span className="text-gray-900 dark:text-white font-medium">{session.dialed_number || '-'}</span>
                    </div>
                    {session.transferred_to && (
                      <div className="flex items-center gap-2 text-sm">
                        <PhoneForwarded className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300 text-xs">{session.transferred_to}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(session.connection_start)}
                    </span>
                    <div className="flex items-center gap-2">
                      {session.transcription && <FileText className="w-3.5 h-3.5 text-blue-600" />}
                      {session.call_summary && <Mic className="w-3.5 h-3.5 text-green-600" />}
                      <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">View</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Bot className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No AI Calls Found</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Try adjusting your filters.</p>
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Call ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Caller ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Dialed Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Transfer Info</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Connection Start</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Connection End</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {data.data.map((session) => {
                const key = rowKey(session);
                // `key !== null` is the guard that matters: without it, a null
                // key on every row matches a null activePlayingId and the whole
                // table opens at once.
                const isPlaying = key !== null && activePlayingId === key;
                const isLoadingPlay = key !== null && loadingPlayIds.has(key);
                const cachedUrl = key !== null ? recordingUrls[key] : undefined;
                const noRecording = cachedUrl === null;
                return (
                <React.Fragment key={rowKey(session) ?? `row-${session.call_log_id}`}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 text-sm text-blue-600 font-medium">CALL-{session.call_log_id}</td>
                  <td className="px-6 py-4 text-sm">
                    {session.call_type ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${session.call_type === 'IN' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400' : 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-400'}`}>
                        {session.call_type === 'IN' ? <PhoneIncoming className="w-3 h-3" /> : <PhoneOutgoing className="w-3 h-3" />}
                        {session.call_type}
                      </span>
                    ) : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    {session.caller_id ? <div className="flex items-center gap-1"><Phone className="w-4 h-4 text-gray-400" />{session.caller_id}</div> : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{session.dialed_number || <span className="text-gray-400">-</span>}</td>
                  <td className="px-6 py-4 text-sm">
                    {session.transfer_type ? (
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${session.transfer_type === 'ai' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-400' : 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400'}`}>
                          {session.transfer_type === 'ai' ? <Bot className="w-3 h-3" /> : <Hash className="w-3 h-3" />}
                          {session.transfer_type === 'ai' ? 'AI' : 'HOTKEY'}
                        </span>
                        {session.transferred_to && <span className="text-xs text-gray-600 dark:text-gray-400"><PhoneForwarded className="w-3 h-3 inline mr-1" />{session.transferred_to}</span>}
                      </div>
                    ) : <span className="text-gray-400">No Transfer</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{formatDuration(session.audio_duration_ms)}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{formatDate(session.connection_start)}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{session.connection_end ? formatDate(session.connection_end) : <span className="text-gray-400">-</span>}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-2">
                      {session.transcription && <span title="Has Transcription"><FileText className="w-4 h-4 text-blue-600" /></span>}
                      {session.call_summary && <span title="Has Summary"><Mic className="w-4 h-4 text-green-600" /></span>}
                      <button
                        onClick={() => handleTogglePlay(session)}
                        disabled={isLoadingPlay || noRecording}
                        title={noRecording ? 'No recording available' : isPlaying ? 'Hide player' : 'Play recording'}
                        className={`w-7 h-7 flex items-center justify-center rounded-full transition ${
                          noRecording
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                            : isPlaying
                              ? 'bg-green-700 text-white'
                              : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {isLoadingPlay ? (
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : isPlaying ? (
                          <Pause className="w-3.5 h-3.5" />
                        ) : (
                          <Play className="w-3.5 h-3.5 ml-0.5" />
                        )}
                      </button>
                      <button onClick={() => setSelectedSession(session)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium ml-2">View Details</button>
                    </div>
                  </td>
                </tr>
                {isPlaying && cachedUrl && (
                  <tr className="bg-gray-50 dark:bg-gray-900/40">
                    <td colSpan={9} className="px-6 py-3">
                      <AudioPlayer src={cachedUrl} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Bot className="w-12 h-12 text-gray-400" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No AI Calls Found</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {(currentRole === 'master_admin' || currentRole === 'super_admin') ? 'Try selecting a different business or adjusting filters.' : 'Try adjusting your date range or filters.'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data.data.length > 0 && <div className="p-3 lg:p-4 border-t border-gray-200 dark:border-gray-700">
          <Pagination currentPage={currentPage} totalPages={data.totalPages} onPageChange={setCurrentPage} totalItems={data.total} itemsPerPage={ITEMS_PER_PAGE} />
        </div>}
      </div>}

      {selectedSession && (
        <AudioSessionModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          organizationId={getOrganizationId()}
        />
      )}
    </div>
  );
};

export default AudioSessions;